import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import '../styles/actionButtons.css';
import { useToasts } from '../contexts/useToasts';
import http from '../api/http';
import AddEducationRecordModal from '../components/AddEducationRecordModal';
import EditEducationRecordModal from '../components/EditEducationRecordModal';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SimplePdfViewer from '../components/SimplePdfViewer';
import AuthedImage from '../components/AuthedImage';
import { buildMinioProxyUrl, downloadWithAuth, openWithAuth } from '../utils/authedResource';

// 全屏预览组件（内部定义）
const FullscreenPreview = ({ open, onClose, attachment, record, decodeFileName }) => {
  if (!open || !attachment) return null;
  const fileUrl = buildMinioProxyUrl(attachment.object_name || attachment.objectName, { filename: attachment.file_name }) || attachment.file_url;
  const rawName = attachment.file_name || attachment.file_url || '附件';
  const displayName = decodeFileName(rawName);
  const isPdf = rawName.toLowerCase().endsWith('.pdf');
  const isImg = /(png|jpe?g|gif|bmp|webp|svg)$/i.test(rawName) || /(png|jpe?g|gif|bmp|webp|svg)$/i.test(fileUrl || '');

  const backdrop = {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.68)', backdropFilter: 'blur(4px)',
    zIndex: 9999, display: 'flex', flexDirection: 'column'
  };
  const header = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0.75rem 1rem', background: 'rgba(30,41,59,0.85)', color: '#fff'
  };
  const body = {
    flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
    padding: '1rem'
  };
  const actions = { display: 'flex', gap: '0.5rem', alignItems: 'center' };
  const btn = {
    background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)',
    borderRadius: '6px', padding: '0.4rem 0.9rem', cursor: 'pointer', fontSize: '0.8rem'
  };

  const onKey = (e) => { if (e.key === 'Escape') onClose(); };

  return (
    <div style={backdrop} onKeyDown={onKey} tabIndex={-1}>
      <div style={header}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <strong style={{ fontSize: '0.95rem' }}>{record?.title || '教育记录'}</strong>
          <span style={{ fontSize: '0.7rem', opacity: 0.85 }}>{displayName}</span>
        </div>
        <div style={actions}>
          {attachment && (
            <button type="button" onClick={()=> openWithAuth(fileUrl)} style={{ ...btn, textDecoration: 'none' }}>原文件</button>
          )}
          {attachment && (
            <button
              type="button"
              onClick={()=> downloadWithAuth(buildMinioProxyUrl(attachment.object_name || attachment.objectName, { download:true, filename: attachment.file_name || displayName }) || fileUrl, attachment.file_name || displayName)}
              style={{ ...btn, textDecoration: 'none' }}
            >下载</button>
          )}
          <button style={{ ...btn, background: 'rgba(239,68,68,0.35)', borderColor: 'rgba(239,68,68,0.55)' }} onClick={onClose}>关闭 (Esc)</button>
        </div>
      </div>
      <div style={body}>
        {isPdf && fileUrl && (
          <div style={{ width: '100%', maxWidth: '1100px' }}>
            <SimplePdfViewer url={fileUrl} fileName={''} />
          </div>
        )}
        {isImg && fileUrl && (
          <AuthedImage src={fileUrl} alt={displayName} style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', background: '#fff' }} />
        )}
        {!isPdf && !isImg && (
          <div style={{ color: '#fff', textAlign: 'center' }}>该类型暂不支持全屏预览。</div>
        )}
      </div>
    </div>
  );
};

const IMAGE_EXT_REGEX = /\.(png|jpe?g|gif|bmp|webp|svg)$/i;
const attachmentIsImage = (fileName = '') => IMAGE_EXT_REGEX.test(fileName);
const attachmentIsPdf = (fileName = '') => fileName.toLowerCase().endsWith('.pdf');
const decodeFileName = (fileName = '') => {
  if (!fileName) return '附件';
  try { return decodeURIComponent(fileName); } catch { return fileName; }
};

// 附件侧栏已移除，缩略图组件删除

const EmployeeFilesPage = () => {
  const location = useLocation();
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedRecordDetail, setSelectedRecordDetail] = useState(null);
  const [selectedRecordType, setSelectedRecordType] = useState(null);
  const [activeTab, setActiveTab] = useState('level3');
  const [activeAttachmentPreview, setActiveAttachmentPreview] = useState(null);
  const [fullscreenState, setFullscreenState] = useState({ open: false, attachment: null, record: null });
  const [pendingDelete, setPendingDelete] = useState({ open: false, recordId: null, loading: false });
  const [editingRecord, setEditingRecord] = useState(null);
  const [attachmentMenuOpen, setAttachmentMenuOpen] = useState(false);
  // 匹配到的 MinIO PDF 档案（按员工+档案类型）
  const [matchedArchives, setMatchedArchives] = useState([]);
  // 用于强制在切换档案时卸载并重新挂载 PDF 预览，避免同一实例状态残留
  const [previewKey, setPreviewKey] = useState(0);
  // 平滑过渡相关：保存上一个档案与淡出状态
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [prevArchive, setPrevArchive] = useState(null);
  const [isCrossfading, setIsCrossfading] = useState(false);
  const [loadingArchives, setLoadingArchives] = useState(false);
  const [archivesError, setArchivesError] = useState('');
  const [deletingArchiveId, setDeletingArchiveId] = useState(null);
  const [confidentArchives, setConfidentArchives] = useState([]);
  const [weakArchives, setWeakArchives] = useState([]);

  const preselectEmployeeIdRef = useRef(null);

  useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search || '');
      const raw = sp.get('employeeId');
      const id = raw ? Number.parseInt(String(raw), 10) : null;
      if (id && Number.isFinite(id)) {
        preselectEmployeeIdRef.current = id;
      }
    } catch (_) {
      // ignore
    }
  }, [location.search]);

  useEffect(() => {
    const id = preselectEmployeeIdRef.current;
    if (!id) return;
    if (!Array.isArray(employees) || employees.length === 0) return;
    if (selectedEmployee && Number(selectedEmployee?.id) === Number(id)) {
      preselectEmployeeIdRef.current = null;
      return;
    }

    const found = employees.find(e => Number(e?.id) === Number(id));
    if (!found) return;
    setSelectedEmployee(found);
    preselectEmployeeIdRef.current = null;
  }, [employees, selectedEmployee]);

  const { addToast } = useToasts();

  const computeMatchMeta = useCallback((fileName = '', employeeName = '') => {
    const normalize = (s) => String(s || '')
      .replace(/\s+/g, '')
      .replace(/[()（）【】\-_—.·,，[\]]/g, '')
      .toLowerCase();

    const cleanFile = normalize(fileName);
    const cleanEmp = normalize(employeeName);

    if (!cleanEmp || !cleanFile) return { score: 0.3, reason: '信息不足' };
    if (cleanFile.includes(cleanEmp)) return { score: 0.95, reason: '包含姓名' };
    if (cleanEmp.includes(cleanFile)) return { score: 0.85, reason: '文件名为姓名' };

    let overlap = 0;
    for (let i = 0; i < cleanEmp.length; i++) {
      for (let j = i + 1; j <= cleanEmp.length; j++) {
        const sub = cleanEmp.slice(i, j);
        if (sub.length < 2) continue;
        if (cleanFile.includes(sub)) overlap = Math.max(overlap, sub.length);
      }
    }
    const overlapRatio = overlap / cleanEmp.length;
    if (overlapRatio >= 0.6) return { score: 0.65 + overlapRatio * 0.2, reason: '部分姓名重叠' };

    const minLen = Math.min(cleanFile.length, cleanEmp.length);
    let diff = 0;
    for (let i = 0; i < minLen; i++) if (cleanFile[i] !== cleanEmp[i]) diff++;
    diff += Math.abs(cleanFile.length - cleanEmp.length);
    const similarity = 1 - Math.min(1, diff / Math.max(1, cleanEmp.length));
    return { score: 0.45 + similarity * 0.25, reason: '弱匹配' };
  }, []);

  // 全屏快捷键：左右翻页 / 上下切换附件（当前基础实现：切换附件）
  useEffect(() => {
    if (!fullscreenState.open) return;
    const handleKey = (e) => {
      if (!fullscreenState.attachment || !fullscreenState.record) return;
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        const atts = fullscreenState.record.attachments || [];
        if (atts.length < 2) return;
        const idx = atts.findIndex(a => a.id === fullscreenState.attachment.id);
        if (idx === -1) return;
        let nextIdx = e.key === 'ArrowUp' ? idx - 1 : idx + 1;
        if (nextIdx < 0) nextIdx = atts.length - 1;
        if (nextIdx >= atts.length) nextIdx = 0;
        setFullscreenState(s => ({ ...s, attachment: atts[nextIdx] }));
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [fullscreenState]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [educationTypeToAdd, setEducationTypeToAdd] = useState('');
  // 移除 hover 状态以避免无意义的重渲染
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('全部');

  const departments = useMemo(() => {
    const values = new Set();
    values.add('全部');
    (employees || []).forEach(emp => {
      const dep = (emp?.department || '').trim();
      if (dep) values.add(dep);
    });
    return Array.from(values);
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    return (employees || []).filter(emp => {
      if (departmentFilter && departmentFilter !== '全部') {
        if ((emp?.department || '') !== departmentFilter) return false;
      }
      if (!term) return true;
      const name = String(emp?.name || '').toLowerCase();
      const no = String(emp?.employee_number || '').toLowerCase();
      return name.includes(term) || no.includes(term);
    });
  }, [employees, searchTerm, departmentFilter]);
  const tabs = [
    { key: 'level3', label: '三级教育档案', type: '三级教育', allowAdd: true },
    { key: 'project', label: '岗前教育档案', type: '岗前教育', allowAdd: true },
    { key: 'daily', label: '日常教育档案', type: '日常教育', allowAdd: true },
    { key: 'occupational', label: '职业健康信息', allowAdd: false },
    { key: 'violation', label: '违章记录', allowAdd: false },
  ];
  const tabForType = {
    '三级教育': 'level3',
    '岗前教育': 'project',
    '日常教育': 'daily',
    '职业健康教育': 'occupational',
    '违章教育': 'violation'
  };

  const fetchEmployees = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await http.get('/employees');
      const list = Array.isArray(response.data) ? response.data : [];
      setEmployees(list);

      // 批量拉取每位员工 5 类档案数量（后端按 archives 表聚合）
      try {
        const ids = list.map(e => e?.id).filter(Boolean);
        if (ids.length) {
          const resp = await http.get(`/employees/archive-counts?ids=${encodeURIComponent(ids.join(','))}`);
          const counts = resp?.data?.counts && typeof resp.data.counts === 'object' ? resp.data.counts : {};
          setEmployees(prev => prev.map(emp => {
            const key = String(emp.id);
            return { ...emp, archive_counts: counts[key] || {} };
          }));
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.response?.data?.error || e?.message;
        // 权限不足或接口异常时不阻塞页面；数量默认显示 0
        if (msg) {
          // 避免刷屏：仅在开发/调试时输出
          // eslint-disable-next-line no-console
          console.warn('[EmployeeFilesPage] archive-counts failed:', msg);

          if (!hasWarnedArchiveCountsRef.current) {
            hasWarnedArchiveCountsRef.current = true;
            const status = e?.response?.status;
            if (status === 403) {
              addToast('无权限获取员工档案数量统计（显示为 0）。请联系管理员分配权限。', 'warning');
            } else {
              addToast('获取员工档案数量统计失败（显示为 0）。', 'warning');
            }
          }
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || '获取员工列表失败。';
      setError(errorMessage);
      addToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [addToast]);
  // 仅在首次挂载时执行一次，避免 fetchEmployees 依赖变化造成重复闪烁
  const hasFetchedRef = useRef(false);
  const hasWarnedArchiveCountsRef = useRef(false);
  // 点击左侧员工卡片内的“数量”时，先记录要跳转的 tab，避免员工切换 effect 把 tab 重置回默认。
  const pendingTabKeyRef = useRef(null);
  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEmployeeDetails = useCallback(async (employeeId) => {
    try {
      const response = await http.get(`/employees/${employeeId}`);
      // 保留列表里已有的 archive_counts，避免点击员工后数量变 0
      setEmployees(prev => prev.map(emp => {
        if (emp.id !== employeeId) return emp;
        const existingCounts = emp.archive_counts;
        return { ...emp, ...response.data, archive_counts: existingCounts };
      }));
      setSelectedEmployee(prev => {
        const existingCounts = prev?.id === employeeId ? prev.archive_counts : undefined;
        return { ...response.data, archive_counts: existingCounts || {} };
      });
      return response.data;
    } catch (err) {
      addToast('获取员工详细档案失败。', 'error');
      return null;
    }
  }, [addToast]);

  // 切换档案类型时重置 PDF 预览，随后由档案加载自动打开最佳匹配
  useEffect(() => {
    setSelectedArchive(null);
    setPrevArchive(null);
    setIsCrossfading(false);
    setPreviewKey(k => k + 1);
  }, [activeTab]);


  // Fetch full profile when an employee is selected
  useEffect(() => {
    if (selectedEmployee && !selectedEmployee.education_records) {
      fetchEmployeeDetails(selectedEmployee.id);
    }
  }, [selectedEmployee, fetchEmployeeDetails]);

  // 根据选中员工与当前 tab 拉取 PDF 档案列表
  useEffect(() => {
    const tabCfg = tabs.find(t => t.key === activeTab);
    const archiveTypeLabel = tabCfg?.label; // e.g. 三级教育档案
    if (!selectedEmployee?.id || !archiveTypeLabel) {
      setMatchedArchives([]); setConfidentArchives([]); setWeakArchives([]); return;
    }
    let canceled = false;
    const load = async () => {
      setLoadingArchives(true); setArchivesError('');
      try {
        const resp = await http.get(`/archives?include_url=1&user_id=${selectedEmployee.id}&archive_type=${encodeURIComponent(archiveTypeLabel)}`);
        if (!canceled) {
          const arr = Array.isArray(resp.data) ? resp.data : [];
          setMatchedArchives(arr);
          // 评分拆分
          const confident = []; const weak = [];
          arr.forEach(a => {
            const meta = computeMatchMeta(a.file_name || '', selectedEmployee.name || '');
            a.__matchMeta = meta;
            if (meta.score >= 0.7) confident.push(a); else weak.push(a);
          });
          setConfidentArchives(confident);
          setWeakArchives(weak);

          // 直接展示预览：自动打开最佳匹配 PDF
          const best = (() => {
            const sortByScoreDesc = (x, y) => (y?.__matchMeta?.score || 0) - (x?.__matchMeta?.score || 0);
            const confidentSorted = [...confident].sort(sortByScoreDesc);
            const weakSorted = [...weak].sort(sortByScoreDesc);
            return confidentSorted[0] || weakSorted[0] || arr[0] || null;
          })();
          if (best) {
            setSelectedArchive(prev => (prev ? prev : best));
          }
        }
      } catch (e) {
        if (!canceled) {
          setArchivesError(e.response?.data?.error || '档案文件读取失败');
          setMatchedArchives([]);
          setConfidentArchives([]); setWeakArchives([]);
        }
      } finally { if (!canceled) setLoadingArchives(false); }
    };
    load();
    return () => { canceled = true; };
  }, [selectedEmployee?.id, selectedEmployee?.name, activeTab, computeMatchMeta]);

  useEffect(() => {
    setSelectedRecordDetail(null);
    setSelectedRecordType(null);
    const nextTab = pendingTabKeyRef.current || 'level3';
    pendingTabKeyRef.current = null;
    setActiveTab(nextTab);
    setActiveAttachmentPreview(null);
    // 关闭并清理当前 PDF 预览（不保留旧档案）
    setSelectedArchive(null);
    setPrevArchive(null);
    setIsCrossfading(false);
    setPreviewKey(k=>k+1);
  }, [selectedEmployee?.id]);

  // 当 tab 或者员工记录列表变化且当前没有选中的记录时，自动选第一条（适用于去掉列表后的场景）
  useEffect(() => {
    if (!selectedEmployee || !selectedEmployee.education_records) return;
    const tabObj = tabs.find(t => t.key === activeTab);
    if (!tabObj || !tabObj.type) return;
    if (selectedRecordDetail && selectedRecordType === tabObj.type) return;
    const records = selectedEmployee.education_records.filter(r => r.education_type === tabObj.type);
    if (records.length) {
      const first = records[0];
      setSelectedRecordDetail(first);
      setSelectedRecordType(first.education_type);
      setActiveAttachmentPreview(getPreferredAttachment(first));
    } else if (selectedRecordDetail) {
      setSelectedRecordDetail(null);
      setSelectedRecordType(null);
      setActiveAttachmentPreview(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedEmployee?.education_records]);

  const handleOpenModal = (type) => {
    setEducationTypeToAdd(type);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEducationTypeToAdd('');
  };

  const handleRecordSubmit = async (recordData, files) => {
    if (!selectedEmployee) return;
    try {
      const response = await http.post(`/employees/${selectedEmployee.id}/education`, recordData);
      const newRecordId = response.data.recordId;
      addToast('教育记录添加成功！', 'success');

      if (files?.length && newRecordId) {
        const formData = new FormData();
        files.forEach(file => formData.append('attachments', file));
        await http.post(`/employees/education/${newRecordId}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        addToast(`成功上传 ${files.length} 个附件！`, 'success');
      }

      const updatedEmployee = await fetchEmployeeDetails(selectedEmployee.id);
      handleCloseModal();

      if (updatedEmployee && newRecordId) {
        const newRecord = updatedEmployee.education_records?.find(r => r.id === newRecordId);
        if (newRecord) {
          const targetTab = tabForType[newRecord.education_type] || 'level3';
          setActiveTab(targetTab);
          setSelectedRecordDetail(newRecord);
          setSelectedRecordType(newRecord.education_type);
          setActiveAttachmentPreview(getPreferredAttachment(newRecord));
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || '操作失败。';
      addToast(errorMessage, 'error');
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!selectedEmployee) return;
    setPendingDelete({ open: true, recordId });
  };

  const handleRecordSelect = (record, type) => {
    setSelectedRecordDetail(record);
    setSelectedRecordType(type);
    setActiveAttachmentPreview(getPreferredAttachment(record));
  };
  const getPreferredAttachment = useCallback((record) => {
    if (!record?.attachments || record.attachments.length === 0) return null;
    const pdfAttachment = record.attachments.find(att => attachmentIsPdf(att.file_name || att.file_url || ''));
    return pdfAttachment || record.attachments[0];
  }, []);

  useEffect(() => {
    if (selectedRecordDetail && selectedRecordType === '三级教育') {
      setActiveAttachmentPreview(prev => {
        const attachments = selectedRecordDetail.attachments || [];
        if (prev && attachments.some(att => att.id === prev.id)) {
          return prev;
        }
        return getPreferredAttachment(selectedRecordDetail);
      });
    } else {
      setActiveAttachmentPreview(null);
    }
  }, [selectedRecordDetail, selectedRecordType, getPreferredAttachment]);

  // --- Styles ---
  const styles = useMemo(() => ({
    pageWrapper: {
      minHeight: '100vh',
      background: '#f5f6fa',
      padding: '0.75rem 1.25rem 1.25rem 1.25rem'
    },
    contentShell: {
      background: '#fff',
      borderRadius: '14px',
      border: '1px solid rgba(15,23,42,0.04)',
      boxShadow: '0 4px 12px rgba(15,23,42,0.04)',
      padding: '0.75rem 1rem 1rem 1rem'
    },
    pageHeader: { display: 'none' },
    container: {
      display: 'flex',
      gap: '1.25rem',
      marginTop: '0.25rem'
    },
    listPanel: {
      width: '340px',
      maxHeight: 'calc(100vh - 160px)',
      overflowY: 'auto',
      background: 'var(--bg-secondary)',
      borderRadius: '12px',
      border: '1px solid rgba(148, 163, 184, 0.18)',
      boxShadow: '0 4px 10px rgba(15,23,42,0.05)',
      padding: '0.6rem'
    },
    listFilter: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.65rem',
      paddingBottom: '0.75rem',
      borderBottom: '1px solid rgba(148,163,184,0.18)',
      marginBottom: '0.75rem'
    },
    quickFilterRow: {
      display: 'flex',
      gap: '0.4rem',
      overflowX: 'auto',
      paddingBottom: '0.1rem'
    },
    quickFilterChip: {
      flex: '0 0 auto',
      fontSize: '0.72rem',
      padding: '0.2rem 0.55rem',
      borderRadius: 999,
      border: '1px solid var(--border-color)',
      background: 'var(--bg-secondary)',
      color: 'var(--text-secondary)',
      cursor: 'pointer'
    },
    quickFilterChipActive: {
      border: '1px solid var(--primary-color)',
      background: 'var(--bg-tertiary)',
      color: 'var(--text-primary)'
    },
    searchInput: {
      width: '100%',
      border: '1px solid rgba(148,163,184,0.35)',
      borderRadius: '10px',
      padding: '0.45rem 0.65rem',
      fontSize: '0.85rem',
      color: 'var(--text-primary)',
      background: '#fff',
      outline: 'none'
    },
    searchSelect: {
      width: '100%',
      border: '1px solid rgba(148,163,184,0.35)',
      borderRadius: '10px',
      padding: '0.45rem 0.55rem',
      fontSize: '0.85rem',
      color: 'var(--text-secondary)',
      background: '#fff',
      outline: 'none'
    },
    listBody: {
      display: 'flex',
      flexDirection: 'column'
    },
    employeeGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
      gap: '0.7rem'
    },
    employeeBillCard: {
      position: 'relative',
      background: '#fff',
      borderRadius: '14px',
      border: '1px solid rgba(148, 163, 184, 0.22)',
      overflow: 'hidden',
      boxShadow: '0 10px 28px rgba(15,23,42,0.06)',
      cursor: 'pointer',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease'
    },
    employeeBillCardActive: {
      border: '1px solid rgba(37,99,235,0.55)',
      boxShadow: '0 12px 30px rgba(37,99,235,0.12)'
    },
    employeeBillBody: {
      padding: '12px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    },
    employeeBillHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '10px'
    },
    employeeBillTitle: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      minWidth: 0
    },
    employeeBillName: {
      fontSize: '15px',
      color: 'var(--text-primary)',
      fontWeight: 800,
      lineHeight: 1.25,
      margin: 0
    },
    employeeBillSub: {
      fontSize: '12px',
      color: 'var(--text-secondary)',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    employeeBillRight: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '8px',
      flex: '0 0 auto'
    },
    employeeBillStamp: {
      width: 44,
      height: 44,
      borderRadius: 999,
      border: '2px solid rgba(37,99,235,0.28)',
      color: 'rgba(37,99,235,0.92)',
      fontWeight: 900,
      fontSize: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      userSelect: 'none',
      flex: '0 0 auto'
    },
    employeeDeptBadge: {
      fontSize: '12px',
      lineHeight: '18px',
      padding: '2px 10px',
      borderRadius: 999,
      border: '1px solid rgba(148,163,184,0.35)',
      background: 'rgba(148,163,184,0.12)',
      color: 'var(--text-secondary)',
      userSelect: 'none'
    },
    employeeCountsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
      gap: '6px'
    },
    employeeCountPill: {
      background: 'var(--bg-secondary)',
      border: '1px solid rgba(148,163,184,0.18)',
      borderRadius: '10px',
      padding: '6px 6px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '2px'
    },
    employeeCountPillClickable: {
      cursor: 'pointer'
    },
    employeeCountLabel: { fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1 },
    employeeCountValue: { fontSize: '14px', fontWeight: 900, color: 'var(--text-primary)', lineHeight: 1.1 },
    contentPanel: {
      flex: 1,
      height: 'calc(100vh - 160px)',
      overflowY: 'auto',
      background: 'var(--bg-primary)',
      border: '1px solid rgba(148, 163, 184, 0.18)',
      borderRadius: '12px',
      padding: '0.75rem 0.85rem'
    },
    sectionHeader: { display: 'none' },
    employeeItem: {
      padding: '0.7rem 0.8rem',
      border: '1px solid transparent',
      borderRadius: '12px',
      marginBottom: '0.6rem',
      cursor: 'pointer',
      transition: 'all 0.25s ease',
      background: 'rgba(148,163,184,0.12)',
      boxShadow: '0 6px 12px rgba(15,23,42,0.06)'
    },
    activeEmployeeItem: {
      padding: '0.7rem 0.8rem',
      border: '1px solid rgba(29,118,252,0.35)',
      borderRadius: '12px',
      marginBottom: '0.6rem',
      cursor: 'pointer',
      transition: 'all 0.25s ease',
      background: 'linear-gradient(135deg, rgba(29,118,252,0.15), rgba(29,118,252,0.05))',
      color: 'var(--brand-primary)',
      boxShadow: '0 10px 24px rgba(29,118,252,0.16)'
    },
    employeeHeader: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '0.5rem 1rem',
      marginBottom: '0.5rem',
      padding: '0.55rem 0.75rem',
      background: 'var(--bg-primary)',
      borderRadius: '10px',
      border: '1px solid rgba(148,163,184,0.12)'
    },
    employeeName: {
      fontSize: '1.25rem',
      fontWeight: 600,
      margin: 0,
      color: 'var(--text-primary)'
    },
    employeeMeta: {
      display: 'flex',
      gap: '1rem',
      color: 'var(--text-secondary)',
      flexWrap: 'wrap',
      fontSize: '0.9rem'
    },
    employeeMetaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.35rem'
    },
    employeeMetaLabel: {
      color: 'var(--text-tertiary)',
      fontWeight: 500
    },
    recordCard: { display: 'none' },
    recordSummaryCard: { display: 'none' },
    summaryInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.35rem'
    },
    summaryTitle: {
      fontSize: '1.05rem',
      fontWeight: 600,
      margin: 0
    },
    summaryMeta: {
      display: 'flex',
      gap: '1.5rem',
      fontSize: '0.9rem',
      color: 'var(--text-secondary)'
    },
    summaryActions: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap',
      justifyContent: 'flex-end'
    },
    recordHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '1rem'
    },
    recordTitle: {
      fontSize: '1.1rem',
      fontWeight: 600,
      margin: 0,
      flex: 1
    },
    recordActions: {
      display: 'flex',
      gap: '0.5rem'
    },
    recordDate: {
      fontSize: '0.9rem',
      color: 'var(--brand-primary)',
      background: 'rgba(29,118,252,0.12)',
      padding: '0.35rem 0.6rem',
      borderRadius: '999px'
    },
    recordContent: {
      color: 'var(--text-primary)',
      lineHeight: 1.7,
      marginBottom: '1rem'
    },
    attachmentSection: {
      marginTop: '1rem',
      paddingTop: '1rem',
      borderTop: '1px dashed rgba(148,163,184,0.5)'
    },
    attachmentList: {
      listStyle: 'none',
      padding: 0,
      margin: '0.5rem 0 0 0'
    },
    attachmentItem: {
      marginBottom: '0.5rem'
    },
    attachmentLink: {
      textDecoration: 'none',
      color: 'var(--brand-primary)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    noRecords: {
      textAlign: 'center',
      padding: '3rem 2rem',
      background: 'var(--bg-secondary)',
      borderRadius: '12px',
      border: '2px dashed rgba(148,163,184,0.4)',
      color: 'var(--text-secondary)'
    },
    detailModalCard: {
      maxHeight: '70vh',
      overflowY: 'auto',
      padding: '0.5rem 0 0'
    },
    detailModalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
      gap: '1rem'
    },
    tabBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
      borderBottom: '1px solid rgba(148,163,184,0.2)',
      marginBottom: '0.85rem',
      paddingBottom: '0.55rem',
      position: 'sticky',
      top: 0,
      background: 'rgba(255,255,255,0.92)',
      backdropFilter: 'blur(6px)',
      zIndex: 5
    },
    tabBarLeft: {
      display: 'flex',
      gap: '0.75rem',
      flexWrap: 'wrap'
    },
    tabButton: {
      border: 'none',
      background: 'transparent',
      padding: '0.65rem 1.2rem',
      borderRadius: '999px',
      cursor: 'pointer',
      fontSize: '0.95rem',
      fontWeight: 500,
      color: 'var(--text-secondary)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.65rem',
      transition: 'all 0.2s ease'
    },
    activeTabButton: {
      background: 'rgba(29,118,252,0.12)',
      color: 'var(--brand-primary)',
      boxShadow: '0 10px 24px rgba(29,118,252,0.18)'
    },
    smallCategoryLabel: { display: 'none' },
    tabBadge: {
      minWidth: '1.75rem',
      padding: '0.1rem 0.5rem',
      borderRadius: '999px',
      background: 'rgba(29,118,252,0.15)',
      color: 'var(--brand-primary)',
      fontSize: '0.8rem',
      textAlign: 'center'
    },
    addRecordButton: {
      border: 'none',
      outline: 'none',
      background: 'linear-gradient(135deg, var(--brand-primary), #5b7bff)',
      color: '#fff',
      padding: '0.7rem 1.6rem',
      borderRadius: '999px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.6rem',
      boxShadow: '0 18px 32px rgba(29,118,252,0.22)',
      transition: 'transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease'
    },
    // addRecordButtonHover 已移除（避免 hover 状态导致组件重渲染）
    addRecordButtonIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '1.6rem',
      height: '1.6rem',
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.22)',
      fontSize: '1rem',
      lineHeight: 1
    },
    recordList: { display: 'none' },
    mediaSection: {
      marginTop: '1.75rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem'
    },
    mediaGroup: {
      background: 'var(--bg-secondary)',
      borderRadius: '12px',
      border: '1px solid rgba(148,163,184,0.25)',
      padding: '1rem 1.25rem'
    },
    mediaGroupTitle: {
      fontWeight: 600,
      marginBottom: '0.75rem',
      color: 'var(--text-primary)'
    },
    mediaGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
      gap: '0.75rem'
    },
    mediaThumb: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.45rem',
      padding: '0.75rem',
      borderRadius: '10px',
      border: '1px solid rgba(148,163,184,0.25)',
      background: 'var(--bg-primary)',
      textDecoration: 'none',
      color: 'inherit',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
    },
    mediaThumbImage: {
      width: '100%',
      height: '100px',
      objectFit: 'cover',
      borderRadius: '8px'
    },
    mediaThumbPlaceholder: {
      width: '100%',
      height: '100px',
      borderRadius: '8px',
      background: 'rgba(148,163,184,0.15)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem',
      color: 'var(--text-secondary)'
    },
    mediaThumbCaption: {
      fontSize: '0.85rem',
      color: 'var(--text-secondary)',
      lineHeight: 1.4,
      wordBreak: 'break-all'
    },
    previewLayout: {
      display: 'block'
    },
    previewMain: {
      background: 'var(--bg-secondary)',
      borderRadius: '12px',
      border: '1px solid rgba(148,163,184,0.16)',
      boxShadow: '0 8px 18px rgba(15,23,42,0.06)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      marginTop: '0.25rem'
    },
    pdfViewerWrapper: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '520px',
      background: 'var(--bg-primary)'
    },
    pdfControls: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '0.75rem',
      padding: '0.75rem 1rem',
      borderBottom: '1px solid rgba(148,163,184,0.2)',
      background: 'var(--bg-primary)'
    },
    pdfControlGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      flexWrap: 'wrap'
    },
    pdfControlButton: {
      border: '1px solid rgba(148,163,184,0.35)',
      background: 'var(--bg-secondary)',
      color: 'var(--text-primary)',
      borderRadius: '10px',
      padding: '0.45rem 0.85rem',
      cursor: 'pointer',
      fontSize: '0.85rem',
      transition: 'all 0.18s ease'
    },
    pdfControlButtonDisabled: {
      cursor: 'not-allowed',
      opacity: 0.55
    },
    pdfPageIndicator: {
      fontSize: '0.9rem',
      color: 'var(--text-secondary)'
    },
    pdfCanvasArea: {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'rgba(15,23,42,0.04)',
      padding: '1rem'
    },
    pdfError: {
      padding: '2rem',
      textAlign: 'center',
      color: 'var(--danger-color)'
    },
    previewFallback: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.75rem',
      padding: '2.5rem 1.5rem',
      color: 'var(--text-secondary)'
    },
    previewMeta: {
      padding: '0.85rem 1.05rem 1rem 1.05rem',
      borderTop: '1px solid rgba(148,163,184,0.2)',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      background: 'var(--bg-primary)'
    },
    attachmentSelectRow: {
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    attachmentSelect: {
      maxWidth: '260px',
      border: '1px solid rgba(148,163,184,0.35)',
      borderRadius: '8px',
      padding: '0.35rem 0.55rem',
      fontSize: '0.8rem',
      background: '#fff',
      color: 'var(--text-primary)'
    },
    recordContentCollapsed: {
      maxHeight: '3.2rem',
      overflow: 'hidden',
      position: 'relative'
    },
    expandToggle: {
      border: 'none',
      background: 'none',
      padding: 0,
      cursor: 'pointer',
      color: 'var(--brand-primary)',
      fontSize: '0.75rem'
    },
    previewActions: {
      marginTop: '0.25rem',
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    previewButton: {
      padding: '0.45rem 0.9rem',
      borderRadius: '10px',
      border: '1px solid rgba(148,163,184,0.35)',
      background: 'var(--bg-secondary)',
      cursor: 'pointer',
      color: 'var(--brand-primary)',
      fontSize: '0.9rem'
    },
    modalOverlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(15,23,42,0.42)',
      backdropFilter: 'blur(3px)',
      WebkitBackdropFilter: 'blur(3px)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1.5rem'
    },
    confirmModal: {
      width: '100%',
      maxWidth: '420px',
      background: 'linear-gradient(180deg,#ffffff,#f8fafc)',
      border: '1px solid rgba(148,163,184,0.35)',
      borderRadius: '14px',
      boxShadow: '0 18px 45px -10px rgba(0,0,0,0.30), 0 4px 12px -2px rgba(0,0,0,0.18)',
      position: 'relative',
      animation: 'fadeZoomIn 160ms ease',
      overflow: 'hidden'
    },
    confirmTitle: {
      margin: 0,
      fontSize: '1rem',
      fontWeight: 600,
      color: 'var(--text-primary)'
    },
    confirmBody: {
      marginTop: '0.65rem',
      fontSize: '0.8rem',
      lineHeight: 1.5,
      color: 'var(--text-secondary)'
    },
    confirmActions: {
      marginTop: '1rem',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.6rem'
    },
    tinyActionBase: {
      fontSize: '0.6rem',
      padding: '0.28rem 0.55rem',
      lineHeight: 1.1,
      borderRadius: '5px',
      border: '1px solid rgba(148,163,184,0.35)',
      background: 'linear-gradient(180deg,#ffffff,#f1f5f9)',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      fontWeight: 500,
      transition: 'background .15s, box-shadow .15s, transform .15s'
    },
    tinyPrimary: {
      color: '#1d4ed8',
      borderColor: 'rgba(59,130,246,0.5)'
    },
    tinySecondary: {
      color: '#334155'
    },
    tinyDanger: {
      color: '#dc2626',
      borderColor: 'rgba(239,68,68,0.5)'
    },
    previewSidebar: { display: 'none' },
    previewSidebarTitle: {
      fontWeight: 600,
      fontSize: '1rem',
      color: 'var(--text-primary)'
    },
    previewSidebarList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.65rem'
    },
    previewSidebarItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.55rem',
      padding: '0.55rem',
      borderRadius: '12px',
      border: '1px solid rgba(148,163,184,0.25)',
      cursor: 'pointer',
      transition: 'all 0.18s ease'
    },
    previewSidebarItemActive: {
      borderColor: 'var(--brand-primary)',
      boxShadow: '0 16px 32px rgba(29,118,252,0.14)',
      background: 'rgba(29,118,252,0.12)'
    },
    previewSidebarThumb: {
      width: '100%',
      height: '150px',
      borderRadius: '10px',
      overflow: 'hidden',
      background: 'rgba(148,163,184,0.16)',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    previewSidebarThumbImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    previewSidebarThumbCanvas: {
      width: '100%',
      height: '100%',
      display: 'block'
    },
    previewSidebarThumbFallback: {
      fontSize: '1.75rem',
      color: 'var(--text-secondary)'
    },
    previewSidebarInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
      fontSize: '0.82rem',
      color: 'var(--text-secondary)'
    },
    previewSidebarName: {
      fontWeight: 600,
      color: 'var(--text-primary)',
      lineHeight: 1.4
    },
    noPreview: {
      padding: '3rem 2rem',
      textAlign: 'center',
      color: 'var(--text-secondary)',
      background: 'var(--bg-secondary)',
      borderRadius: '14px',
      border: '2px dashed rgba(148,163,184,0.4)'
    },
    healthCard: {
      background: 'var(--bg-secondary)',
      borderRadius: '16px',
      border: '1px solid rgba(148,163,184,0.25)',
      padding: '1.75rem',
      boxShadow: '0 20px 40px rgba(15,23,42,0.12)',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem'
    },
    healthRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '1rem'
    },
    healthLabel: {
      fontWeight: 600,
      color: 'var(--text-primary)'
    },
    healthValue: {
      color: 'var(--text-secondary)',
      flex: 1,
      textAlign: 'right'
    },
    violationCard: {
      background: 'rgba(255, 245, 245, 0.9)',
      border: '1px dashed rgba(244, 63, 94, 0.35)',
      borderRadius: '16px',
      padding: '2rem',
      color: 'rgba(220,38,38,0.9)',
      textAlign: 'center',
      lineHeight: 1.6
    },
    inlineDetail: { display: 'none' }
  }), []);

  // 通用预览组件（合并三级/岗前/日常），避免多份重复逻辑
  const RecordPreview = React.memo(({ record }) => {
    if (!record) return null;
    const attachments = record.attachments || [];
    const activeAttachment = activeAttachmentPreview || getPreferredAttachment(record);
    const rawFileName = activeAttachment?.file_name || activeAttachment?.file_url || '附件';
    const displayFileName = decodeFileName(rawFileName);
    const fileUrl = activeAttachment?.file_url;
    const isPdf = activeAttachment && (attachmentIsPdf(rawFileName) || attachmentIsPdf(fileUrl || ''));
    const isImage = activeAttachment && (attachmentIsImage(rawFileName) || attachmentIsImage(fileUrl || ''));

    const allSameType = (selectedEmployee?.education_records || []).filter(r => r.education_type === record.education_type);
  const multiple = allSameType.length > 1;

    // 内部展开状态 Map（复用原逻辑）
    if (!RecordPreview._expandedMap) RecordPreview._expandedMap = new Map();
    const isExpanded = RecordPreview._expandedMap.get(record.id) || false;
    const toggleExpanded = () => {
      RecordPreview._expandedMap.set(record.id, !isExpanded);
      setActiveAttachmentPreview(a => a ? { ...a } : a);
    };

    const renderMain = () => {
      if (!activeAttachment) {
        return (
          <div style={styles.noPreview}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📄</div>
            <div>未找到可预览的附件。</div>
          </div>
        );
      }
      if (isPdf && fileUrl) return <SimplePdfViewer url={fileUrl} fileName={''} />;
      if (isImage && fileUrl) return (
        <div style={{ ...styles.previewFallback, padding: 0 }}>
          <AuthedImage src={fileUrl} alt={displayFileName} style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#fff' }} />
        </div>
      );
      return (
        <div style={styles.previewFallback}>
          <span style={{ fontSize: '2.4rem' }}>📄</span>
          <div>暂不支持该附件类型预览。</div>
          {fileUrl && (
            <button
              type="button"
              onClick={()=> openWithAuth(fileUrl)}
              className="button button-secondary button-small"
            >打开原文件</button>
          )}
        </div>
      );
    };

    const cleanedContent = (record.content || '').replace(/\n{3,}/g,'\n\n');
    return (
      <div style={styles.previewLayout}>
        <div style={styles.previewMain}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0.75rem 0 0.75rem', background: 'var(--bg-secondary)', borderBottom: '1px solid rgba(148,163,184,0.2)', gap: '0.75rem', alignItems: 'center' }}>
            {multiple ? (
              <select
                value={record.id}
                onChange={(e) => {
                  const next = allSameType.find(r => String(r.id) === e.target.value);
                  if (next) handleRecordSelect(next, next.education_type);
                }}
                style={{ fontSize: '0.72rem', padding: '0.35rem 0.55rem', borderRadius: '6px', border: '1px solid rgba(148,163,184,0.45)', background: 'var(--bg-primary)', color: 'var(--text-primary)', maxWidth: '280px' }}
              >
                {allSameType.map((r, idx) => {
                  const dateStr = r.education_date ? new Date(r.education_date).toLocaleDateString() : '日期';
                  const title = r.title?.trim() || `记录#${idx + 1}`;
                  const label = `${idx + 1}. ${dateStr} ${title.length > 18 ? title.slice(0,18) + '…' : title}`;
                  return <option key={r.id} value={r.id}>{label}</option>;
                })}
              </select>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', padding: '0.2rem 0.1rem' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 600 }}>{record.title || '教育记录'}</span>
                <span style={{ fontSize: '0.6rem', opacity: 0.7 }}>{record.education_date ? new Date(record.education_date).toLocaleDateString() : '—'}</span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'nowrap' }}>
              <span style={{ fontSize: '0.6rem', opacity: 0.65, minWidth: '3.5rem', textAlign: 'right' }}>{record.education_type}</span>
              <div className="tiny-action-group">
                <button
                  type="button"
                  className="tiny-action danger"
                  aria-label="删除该记录"
                  title="删除该记录"
                  onClick={() => { setAttachmentMenuOpen(false); handleDeleteRecord(record.id); }}
                >删除</button>
                <button
                  type="button"
                  className="tiny-action"
                  aria-label="编辑记录"
                  title="编辑记录"
                  onClick={() => { setEditingRecord(record); setAttachmentMenuOpen(false); }}
                >编辑</button>
                {activeAttachment && (
                  <div style={{ position: 'relative' }} className="attachment-menu-container">
                    <button
                      type="button"
                      className="tiny-action primary"
                      aria-haspopup="true"
                      aria-expanded={attachmentMenuOpen ? 'true' : 'false'}
                      title="查看或下载附件"
                      onClick={() => setAttachmentMenuOpen(prev => !prev)}
                    >附件 ▾</button>
                    {attachmentMenuOpen && (
                      <div style={{
                        position: 'absolute',
                        top: '108%',
                        right: 0,
                        minWidth: '120px',
                        background: '#fff',
                        border: '1px solid rgba(148,163,184,0.35)',
                        boxShadow: '0 6px 18px -4px rgba(0,0,0,0.12)',
                        borderRadius: '8px',
                        padding: '0.35rem 0',
                        zIndex: 40
                      }}>
                        <button
                          type="button"
                          className="tiny-action"
                          style={{ width: '100%', justifyContent: 'flex-start', border: 'none', background: 'transparent', borderRadius: 0 }}
                          onClick={async () => { try { await openWithAuth(activeAttachment.file_url); } finally { setAttachmentMenuOpen(false); } }}
                        >🔗 原文件</button>
                        <button
                          type="button"
                          className="tiny-action"
                          style={{ width: '100%', justifyContent: 'flex-start', border: 'none', background: 'transparent', borderRadius: 0 }}
                          onClick={async () => { try { await downloadWithAuth(activeAttachment.file_url, activeAttachment.file_name || displayFileName); } finally { setAttachmentMenuOpen(false); } }}
                        >⬇️ 下载</button>
                      </div>
                    )}
                  </div>
                )}
                {activeAttachment && (
                  <button
                    type="button"
                    className="tiny-action"
                    aria-label="全屏预览附件"
                    title="全屏预览附件"
                    onClick={() => setFullscreenState({ open: true, attachment: activeAttachment, record })}
                  >全屏</button>
                )}
              </div>
            </div>
          </div>
          {renderMain()}
          <div style={styles.previewMeta}>
            <div style={styles.attachmentSelectRow}>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>教育日期：{record.education_date ? new Date(record.education_date).toLocaleDateString() : '—'}</div>
              {attachments.length > 1 && (
                <select
                  value={activeAttachment?.id || ''}
                  onChange={(e) => {
                    const att = attachments.find(a => String(a.id) === e.target.value);
                    if (att) setActiveAttachmentPreview(att);
                  }}
                  style={styles.attachmentSelect}
                >
                  {attachments.map(att => {
                    const name = decodeFileName(att.file_name || att.file_url || '附件');
                    const lower = name.toLowerCase();
                    let icon = '📄';
                    if (/(pdf)$/.test(lower)) icon = '📕';
                    else if (/(png|jpe?g|gif|bmp|webp|svg)$/.test(lower)) icon = '🖼️';
                    else if (/(xls|xlsx|csv)$/.test(lower)) icon = '📊';
                    else if (/(ppt|pptx)$/.test(lower)) icon = '🖥️';
                    else if (/(doc|docx)$/.test(lower)) icon = '📝';
                    return <option key={att.id} value={att.id}>{`${icon} ${name}`}</option>;
                  })}
                </select>
              )}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.55, position: 'relative' }}>
              <div style={!isExpanded ? styles.recordContentCollapsed : undefined}>
                {cleanedContent ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}
                    components={{
                      code: ({inline, className, children, ...props}) => (
                        <code style={{
                          background: 'rgba(148,163,184,0.18)',
                          padding: inline ? '0.1rem 0.3rem' : '0.5rem',
                          borderRadius: '6px',
                          display: inline ? 'inline' : 'block',
                          fontSize: '0.7rem'
                        }} {...props}>{children}</code>
                      ),
                      a: ({children, ...p}) => <a {...p} style={{ color: '#2563eb' }}>{children}</a>,
                      h1: ({children}) => <h4 style={{ fontSize: '0.9rem' }}>{children}</h4>,
                      h2: ({children}) => <h5 style={{ fontSize: '0.85rem' }}>{children}</h5>,
                      table: ({children}) => <div style={{overflowX:'auto'}}><table style={{borderCollapse:'collapse', width:'100%', fontSize:'0.72rem'}}>{children}</table></div>,
                      th: ({children}) => <th style={{border:'1px solid rgba(148,163,184,0.4)', padding:'0.25rem 0.4rem', background:'rgba(148,163,184,0.15)'}}>{children}</th>,
                      td: ({children}) => <td style={{border:'1px solid rgba(148,163,184,0.25)', padding:'0.25rem 0.4rem'}}>{children}</td>
                    }}
                  >{cleanedContent}</ReactMarkdown>
                ) : '无内容摘要。'}
              </div>
              {record.content && record.content.length > 80 && (
                <button type="button" style={styles.expandToggle} onClick={toggleExpanded}>{isExpanded ? '收起' : '展开'}</button>
              )}
            </div>
            {/* 操作按钮已移至顶部下拉右侧 */}
          </div>
        </div>
      </div>
    );
  });
  // 简化后不再需要独立的普通记录详情卡片

  // --- Render Methods ---
  const renderEmployeeList = () => {
    if (isLoading) return <p>正在加载员工列表...</p>;
    if (error) return <p style={{ color: 'var(--danger-color)' }}>{error}</p>;
    if (employees.length === 0) return <p>未找到任何员工。</p>;

    if (filteredEmployees.length === 0) {
      return <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>未找到符合条件的员工。</p>;
    }

    return (
      <div style={styles.employeeGrid}>
        {filteredEmployees.map(emp => {
          const isActive = selectedEmployee?.id === emp.id;
          const initial = (emp.name || '').trim().slice(0, 1) || '员';

          const counts = emp.archive_counts && typeof emp.archive_counts === 'object' ? emp.archive_counts : {};
          const getCount = (archiveType) => Number(counts?.[archiveType] || 0);
          const countItems = [
            { short: '三级', type: '三级教育档案' },
            { short: '岗前', type: '岗前教育档案' },
            { short: '日常', type: '日常教育档案' },
            { short: '职业', type: '职业健康信息' },
            { short: '违章', type: '违章记录' },
          ];

          const archiveTypeToTabKey = {
            '三级教育档案': 'level3',
            '岗前教育档案': 'project',
            '日常教育档案': 'daily',
            '职业健康信息': 'occupational',
            '违章记录': 'violation'
          };

          const handleCountClick = (e, archiveType) => {
            e.preventDefault();
            e.stopPropagation();
            const tabKey = archiveTypeToTabKey[archiveType] || 'level3';

            // 同一员工卡片内切换档案类型时，selectedEmployee 引用可能不变，
            // 仅 setSelectedEmployee 会导致切换无效，因此直接切换 activeTab。
            if (selectedEmployee?.id === emp.id) {
              pendingTabKeyRef.current = null;
              setActiveTab(tabKey);
              return;
            }

            pendingTabKeyRef.current = tabKey;
            setSelectedEmployee(emp);
          };

          return (
            <div
              key={emp.id}
              style={{
                ...styles.employeeBillCard,
                ...(isActive ? styles.employeeBillCardActive : {})
              }}
              onClick={() => setSelectedEmployee(emp)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setSelectedEmployee(emp);
              }}
            >
              <div style={styles.employeeBillBody}>
                <div style={styles.employeeBillHeader}>
                  <div style={styles.employeeBillTitle}>
                    <p style={styles.employeeBillName}>{emp.name}</p>
                    <div style={styles.employeeBillSub}>
                      {emp.employee_number ? `工号：${emp.employee_number}` : '工号：—'}
                    </div>
                  </div>
                  <div style={styles.employeeBillRight}>
                    <div style={styles.employeeBillStamp}>{initial}</div>
                    {emp.department ? (
                      <div style={styles.employeeDeptBadge}>{emp.department}</div>
                    ) : (
                      <div style={styles.employeeDeptBadge}>—</div>
                    )}
                  </div>
                </div>

                <div style={styles.employeeCountsGrid}>
                  {countItems.map(item => (
                    <div
                      key={item.type}
                      role="button"
                      tabIndex={0}
                      title={`查看${item.short}档案`}
                      style={{ ...styles.employeeCountPill, ...styles.employeeCountPillClickable }}
                      onClick={(e) => handleCountClick(e, item.type)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') handleCountClick(e, item.type);
                      }}
                    >
                      <span style={styles.employeeCountLabel}>{item.short}</span>
                      <span style={styles.employeeCountValue}>{getCount(item.type)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderEmployeeDetails = () => {
    if (!selectedEmployee) {
      return <div style={{ textAlign: 'center', color: 'var(--text-secondary)', marginTop: '4rem' }}>请从左侧选择一位员工以查看其安全档案。</div>;
    }

    if (!selectedEmployee.education_records) {
      return <p>正在加载 {selectedEmployee.name} 的详细档案...</p>;
    }

    const activeTabConfig = tabs.find(tab => tab.key === activeTab) || tabs[0];

    const typeMapping = {
      level3: activeTabConfig.type,
      project: activeTabConfig.type,
      daily: activeTabConfig.type,
      occupational: '职业健康教育',
      violation: '违章教育'
    };
    const mappedEducationType = typeMapping[activeTab];

    const openArchive = (a) => {
      if (!a) return;
      if (selectedArchive?.id === a.id) return;
      if (selectedArchive) setPrevArchive(selectedArchive);
      setSelectedArchive(a);
      setPreviewKey(k => k + 1);
      setIsCrossfading(true);
      window.setTimeout(() => {
        setPrevArchive(null);
        setIsCrossfading(false);
      }, 300);
    };

    const removeArchive = async (e, a) => {
      e.stopPropagation();
      if (!window.confirm('确认删除该档案及其文件？此操作不可撤销。')) return;
      setDeletingArchiveId(a.id);
      try {
        await http.delete(`/archives/${a.id}`);
        const filtered = matchedArchives.filter(x => x.id !== a.id);
        setMatchedArchives(filtered);
        setConfidentArchives(confidentArchives.filter(x => x.id !== a.id));
        setWeakArchives(weakArchives.filter(x => x.id !== a.id));
        if (selectedArchive?.id === a.id) {
          setSelectedArchive(null);
          setPrevArchive(null);
        }
      } catch (err) {
        alert(err.response?.data?.error || err.message || '删除失败');
      } finally {
        setDeletingArchiveId(null);
      }
    };

    return (
      <div>
        {/* 粘性操作区：在右侧独立滚动时保持可用 */}
        <div style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-primary)', paddingTop: '0.25rem', paddingBottom: '0.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="button button-primary button-small add-record-btn"
              style={{ ...styles.addRecordButton, padding: '0.5rem 1.2rem' }}
              onClick={() => handleOpenModal(mappedEducationType)}
            >
              <span style={{ ...styles.addRecordButtonIcon, width: '1.3rem', height: '1.3rem' }}>＋</span>
              <span style={{ fontSize: '0.8rem' }}>添加记录</span>
            </button>
          </div>
        </div>

        {/* 预览区域置顶 */}
        {(selectedArchive || prevArchive) && (
          <div style={{ marginTop: '0.4rem', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '0.75rem', position: 'relative', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.55rem' }}>
              <strong style={{ fontSize: '.8rem' }}>{(selectedArchive || prevArchive).file_name || 'PDF 档案预览'}</strong>
              <div style={{ display: 'flex', gap: '.4rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedArchive(null);
                    setPrevArchive(null);
                    setIsCrossfading(false);
                  }}
                  style={{ fontSize: '.6rem', background: 'var(--border-strong)', color: 'var(--text-primary)', border: 'none', padding: '.35rem .55rem', borderRadius: 4, cursor: 'pointer' }}
                >
                  关闭
                </button>
                {selectedArchive && (
                  <button
                    type="button"
                    onClick={() => openWithAuth(selectedArchive.file_url)}
                    style={{ fontSize: '.6rem', textDecoration: 'none', background: 'var(--primary-color)', color: '#fff', padding: '.35rem .55rem', borderRadius: 4, border: 'none', cursor: 'pointer' }}
                  >
                    新窗口
                  </button>
                )}
              </div>
            </div>
            {/* 双层叠放：旧档案淡出，新档案淡入 */}
            {prevArchive && (
              <div style={{ position: 'absolute', inset: 0, transition: 'opacity 0.3s', opacity: isCrossfading ? 0 : 1 }} aria-hidden="true">
                <SimplePdfViewer key={`prev-${previewKey}`} url={prevArchive.file_url} fileName={prevArchive.file_name || ''} />
              </div>
            )}
            {selectedArchive && (
              <div style={{ position: 'relative', transition: 'opacity 0.3s', opacity: isCrossfading ? 1 : (prevArchive ? 0 : 1) }}>
                <SimplePdfViewer key={`cur-${previewKey}`} url={selectedArchive.file_url} fileName={selectedArchive.file_name || ''} />
              </div>
            )}
          </div>
        )}

        {/* 状态提示 */}
        {loadingArchives && (
          <div style={{ padding: '0.6rem 0.2rem', color: 'var(--text-secondary)', fontSize: '.8rem' }}>正在加载档案文件...</div>
        )}
        {!loadingArchives && !!archivesError && (
          <div style={{ padding: '0.6rem 0.2rem', color: 'var(--danger-color)', fontSize: '.8rem' }}>{archivesError}</div>
        )}
        {!loadingArchives && !archivesError && !matchedArchives.length && (
          <div style={{ padding: '1.2rem 0.2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>暂无可预览的 PDF 档案</div>
        )}

        {/* 简化文件列表：用于切换/删除（放在预览下方） */}
        {!!matchedArchives.length && (
          <div style={{ marginTop: '0.75rem' }}>
            <div style={{ fontSize: '.75rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
              <strong style={{ fontSize: '.75rem', color: 'var(--text-secondary)' }}>PDF 列表</strong>
              <span style={{ fontSize: '.7rem' }}>{matchedArchives.length} 份</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.6rem' }}>
              {matchedArchives.map(a => {
                const isSelected = selectedArchive?.id === a.id;
                return (
                  <div
                    key={a.id}
                    onClick={() => openArchive(a)}
                    title="点击预览"
                    style={{
                      cursor: 'pointer',
                      background: 'var(--bg-secondary)',
                      border: isSelected ? `1px solid var(--primary-color)` : '1px solid var(--border-color)',
                      borderRadius: 8,
                      padding: '0.65rem 0.75rem',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: '0.6rem'
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--text-primary)', wordBreak: 'break-all' }}>
                        {a.file_name || '未命名.pdf'}
                      </div>
                      {a.created_at && (
                        <div style={{ marginTop: '0.25rem', fontSize: '.7rem', color: 'var(--text-secondary)' }}>
                          {new Date(a.created_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={deletingArchiveId === a.id}
                      onClick={(e) => removeArchive(e, a)}
                      style={{
                        flex: '0 0 auto',
                        fontSize: '.7rem',
                        color: 'var(--danger-color)',
                        background: 'transparent',
                        border: `1px solid var(--danger-color)`,
                        padding: '0.35rem 0.55rem',
                        borderRadius: 6,
                        cursor: 'pointer'
                      }}
                    >
                      {deletingArchiveId === a.id ? '删除中...' : '删除'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.pageWrapper}>
      <div style={styles.contentShell}>
  {/* 顶部标题已隐藏以压缩垂直空间 */}
        <div style={styles.container}>
          <div style={styles.listPanel}>
            <div style={styles.listFilter}>
              <input
                type="search"
                placeholder="搜索姓名或工号"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
                aria-label="按姓名或工号搜索员工"
              />
              <div style={styles.quickFilterRow} aria-label="部门快捷筛选">
                {departments.slice(0, 10).map(option => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setDepartmentFilter(option)}
                    style={{
                      ...styles.quickFilterChip,
                      ...(departmentFilter === option ? styles.quickFilterChipActive : {})
                    }}
                    title={option}
                  >
                    {option}
                  </button>
                ))}
              </div>
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                style={styles.searchSelect}
                aria-label="按部门筛选"
              >
                {departments.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div style={styles.listBody}>
              {renderEmployeeList()}
            </div>
          </div>
          <div style={styles.contentPanel}>
            {renderEmployeeDetails()}
          </div>
        </div>
        {isModalOpen && selectedEmployee && (
          <AddEducationRecordModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onSubmit={handleRecordSubmit}
            employeeId={selectedEmployee.id}
            educationType={educationTypeToAdd}
          />
        )}
        {editingRecord && selectedEmployee && (
          <EditEducationRecordModal
            isOpen={!!editingRecord}
            onClose={() => setEditingRecord(null)}
            record={editingRecord}
            onSubmit={async (data) => {
              try {
                await http.put(`/employees/education/${editingRecord.id}`, data);
                addToast('记录更新成功！', 'success');
                const refreshed = await fetchEmployeeDetails(selectedEmployee.id);
                if (refreshed) {
                  const updated = refreshed.education_records.find(r => r.id === editingRecord.id);
                  setSelectedEmployee(refreshed);
                  if (updated) {
                    setSelectedRecordDetail(updated);
                    setActiveAttachmentPreview(getPreferredAttachment(updated));
                  }
                }
                setEditingRecord(null);
              } catch (e) {
                addToast(e.response?.data?.error || '更新失败', 'error');
              }
            }}
          />
        )}
        <FullscreenPreview
          open={fullscreenState.open}
          onClose={() => setFullscreenState({ open: false, attachment: null, record: null })}
          attachment={fullscreenState.attachment}
          record={fullscreenState.record}
          decodeFileName={decodeFileName}
        />
        {pendingDelete.open && (
          <div
            style={styles.modalOverlay}
            role="dialog"
            aria-modal="true"
            onClick={() => !pendingDelete.loading && setPendingDelete({ open: false, recordId: null, loading: false })}
            onKeyDown={(e) => { if (e.key === 'Escape' && !pendingDelete.loading) setPendingDelete({ open: false, recordId: null, loading: false }); }}
            tabIndex={-1}
          >
            <div
              style={{ ...styles.confirmModal, padding: '1rem 1.1rem 0.9rem' }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ ...styles.confirmTitle, display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: 0 }}>
                <span style={{ fontSize: '1.25rem' }}>⚠️</span>
                确认删除记录？
              </h3>
              <div style={{ ...styles.confirmBody, fontSize: '0.8rem', lineHeight: 1.5, color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                此操作将永久移除该教育记录及其所有附件文件，删除后无法恢复。请再次确认是否执行该危险操作。
              </div>
              <div style={{ ...styles.confirmActions, marginTop: '0.9rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => setPendingDelete({ open: false, recordId: null, loading: false })}
                  disabled={pendingDelete.loading}
                  style={{ minWidth: '80px' }}
                >
                  取消
                </button>
                <button
                  type="button"
                  className="button button-danger"
                  style={{ opacity: pendingDelete.loading ? 0.7 : 1, minWidth: '80px' }}
                  onClick={async () => {
                    if (!pendingDelete.recordId || !selectedEmployee) return;
                    try {
                      setPendingDelete(p => ({ ...p, loading: true }));
                      await http.delete(`/employees/education/${pendingDelete.recordId}`);
                      addToast('记录删除成功！', 'success');
                      const refreshed = await fetchEmployeeDetails(selectedEmployee.id);
                      await fetchEmployees();
                      if (selectedRecordDetail?.id === pendingDelete.recordId) {
                        const remaining = refreshed?.education_records?.filter(r => r.education_type === selectedRecordType && r.id !== pendingDelete.recordId) || [];
                        if (remaining.length) {
                          const next = remaining[0];
                          setSelectedRecordDetail(next);
                          setActiveAttachmentPreview(getPreferredAttachment(next));
                        } else {
                          setSelectedRecordDetail(null);
                          setSelectedRecordType(null);
                          setActiveAttachmentPreview(null);
                        }
                      }
                      setPendingDelete({ open: false, recordId: null, loading: false });
                    } catch (err) {
                      addToast(err.response?.data?.error || err.message || '删除失败。', 'error');
                      setPendingDelete({ open: false, recordId: null, loading: false });
                    }
                  }}
                  disabled={pendingDelete.loading}
                >
                  {pendingDelete.loading ? '删除中...' : '确认删除'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeFilesPage;
